/**
 * Палитра и стили графиков — Soft Slate (AUFB-67).
 *
 * До этого в проекте было шесть независимых массивов CHART_COLORS
 * (AccountingPage, DashboardPage, HomePage, StoreSalesPage, WarehousePage,
 * WarehouseRevisionReportsPage) плюс двенадцатицветный PIE_PALETTE в
 * AccountingPnlPage. Одна и та же сущность красилась по-разному на разных
 * страницах.
 *
 * Значения приходят из CSS-переменных, поэтому серии сами меняются вместе
 * с темой — recharts корректно принимает var() в fill/stroke (это уже
 * работало в AssistantChart).
 *
 * ВАЖНО про порядок и количество.
 * Порядок фиксирован: первая серия всегда синяя, вторая всегда оранжевая,
 * независимо от фильтров — иначе при смене набора данных цвет «переезжает»
 * с одной сущности на другую и график читается неверно.
 *
 * Серий ровно пять, и это не случайность: палитра проверена валидатором на
 * протанопию, дейтеранопию и тританопию, а также на контраст к светлой и
 * тёмной поверхностям. Расширять её «на глаз» нельзя. Если категорий больше
 * пяти — не цикл по модулю (даст два одинаковых цвета в одной легенде), а
 * либо topWithOther(), либо бар-чарт одного цвета с подписями категорий.
 *
 * Фирменный жёлтый в серии не входит: пока он был первым цветом графиков,
 * он встречался на дашборде в десятке мест и переставал читаться как цвет
 * бренда. Жёлтый — логотип и активный пункт меню.
 */

/** Категориальные серии. Индекс = порядковый номер серии. */
export const CHART_SERIES = [
  "var(--c-s1)",
  "var(--c-s2)",
  "var(--c-s3)",
  "var(--c-s4)",
  "var(--c-s5)",
];

export const MAX_SERIES = CHART_SERIES.length;

/** Цвет для агрегата «Прочее» и для нейтральных сегментов. */
export const OTHER_COLOR = "var(--c-text-muted)";

/**
 * Цвет серии по индексу. За пределами палитры возвращает нейтральный серый,
 * а НЕ повторяет цвет по кругу: одинаковые цвета в одной легенде хуже, чем
 * честный «прочее».
 */
export function seriesColor(index) {
  return CHART_SERIES[index] ?? OTHER_COLOR;
}

/** Статусные цвета. Никогда не используются как цвет серии. */
export const STATUS_COLORS = Object.freeze({
  success: "var(--c-success)",
  danger: "var(--c-danger)",
  warning: "var(--c-warning)",
});

/* ── Оформление осей и подсказок ──────────────────────────── */

export const gridStyle = {
  stroke: "var(--c-border)",
  strokeDasharray: "3 3",
};

export const axisStyle = {
  stroke: "var(--c-border)",
  tick: { fill: "var(--c-text-muted)", fontSize: 13 },
};

export const tooltipStyle = {
  background: "var(--c-card)",
  border: "1px solid var(--c-border)",
  borderRadius: 10,
  boxShadow: "var(--c-shadow-sm)",
  color: "var(--c-text)",
  fontSize: 14,
};

export const tooltipItemStyle = { color: "var(--c-text)" };
export const tooltipLabelStyle = { color: "var(--c-text-muted)" };

/* ── Агрегация ────────────────────────────────────────────── */

/**
 * Сворачивает длинный список категорий в «топ-N + Прочее».
 *
 * Нужен там, где категорий больше, чем различимых цветов. Возвращает новый
 * массив, исходный не мутирует.
 *
 * @param {Array<object>} rows      исходные категории
 * @param {string}        valueKey  поле с числом
 * @param {string}        labelKey  поле с названием
 * @param {number}        limit     сколько категорий показать отдельно
 */
export function topWithOther(rows, valueKey, labelKey, limit = MAX_SERIES) {
  if (!Array.isArray(rows) || rows.length <= limit) return rows || [];

  const sorted = [...rows].sort(
    (a, b) => Number(b?.[valueKey] || 0) - Number(a?.[valueKey] || 0)
  );
  const head = sorted.slice(0, limit);
  const tail = sorted.slice(limit);
  const rest = tail.reduce((sum, r) => sum + Number(r?.[valueKey] || 0), 0);

  if (!rest) return head;

  return [
    ...head,
    { [labelKey]: `Прочее (${tail.length})`, [valueKey]: rest, __isOther: true },
  ];
}
